/* eslint-disable camelcase */
/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import React, { useState } from "react";
import { gql } from "graphql-tag";
import { useQuery, useMutation } from "@apollo/client";
import { Link, navigate, RouteComponentProps } from "@reach/router";
import Modal from "components/modal/message-modal";
import { LoadingSpinner, ServerError } from "components/graphql-views";
import {
  updateTemplateGroup,
  updateTemplateGroupVariables,
  DuplicateViewOnlyTemplateGroup,
  DuplicateViewOnlyTemplateGroupVariables,
  TemplateGroupCreateInput,
} from "types/gql-types";
import logger from "services/logger";
import { useSnackbar } from "components/haven-design/snackbar";
import { Viewer } from "components/auth/types";
import { calculateMessageValues } from "utils/template";
import { TEMPLATE_DASHBOARD_ROUTE } from "../template-groups";
import styles from "./template-group.module.css";
import { TemplateForm } from "./form";

const SERVER_ERROR =
  "Unable to update template. Please try again or contact support@havenconnect.com.";

const UPDATE_TEMPLATE_GROUP = gql`
  mutation updateTemplateGroup($id: ID!, $data: TemplateGroupCreateInput!) {
    updateTemplateGroup(id: $id, data: $data) {
      id
    }
  }
`;

const DUPLICATE_TEMPLATE = gql`
  mutation DuplicateViewOnlyTemplateGroup($id: ID!) {
    duplicateTemplateGroup(id: $id) {
      id
    }
  }
`;

const TEMPLATE = gql`
  query getTemplateGroup($id: ID!) {
    templateGroup(id: $id) {
      id
      name
      isOrgTemplate
      isPropertyTemplate
      properties {
        id
        interestListId
        name
      }
      canModify
      templates {
        id
        type
        html
        body
        subject
        isEmpty
      }
    }
  }
`;

const gqlUserToOption = (x: { id: string; name: string; interestListId: string }) => ({
  id: x.interestListId,
  label: x.name,
});

const UpdateTemplateGroups: React.FC<RouteComponentProps & {id?: string, viewer: Viewer}> =
(props) => {
  if (!props.id) return <ServerError desc="Missing ID" />;
  const { loading, data, error } = useQuery(TEMPLATE, {
    variables: { id: props.id },
    fetchPolicy: "network-only",
  });

  const [updateTemplateGroupFunc, updateTemplateGroupMutation] = useMutation<
  updateTemplateGroup,
  updateTemplateGroupVariables
  >(UPDATE_TEMPLATE_GROUP, { refetchQueries: ["myTemplateGroups"] });
  const [duplicate] = useMutation<
  DuplicateViewOnlyTemplateGroup,
  DuplicateViewOnlyTemplateGroupVariables
  >(DUPLICATE_TEMPLATE);
  const [serverError, setServerError] = useState(false);
  const { openSnackbar } = useSnackbar();

  if (error) return <ServerError desc={error.message} />;
  if (loading) return <LoadingSpinner />;
  if (!data) return <ServerError desc="Missing Data" />;
  if (!data.templateGroup) return <ServerError desc="Missing Data" />;

  const closeModal = () => {
    setServerError(false);
  };

  const renderErrorModal = () => (
    <Modal
      isOpen={serverError}
      onSubmit={closeModal}
      onDismiss={closeModal}
      message={SERVER_ERROR}
    />
  );

  const template = data.templateGroup;
  const { canModify } = data.templateGroup;

  const onSubmit = (templateData: TemplateGroupCreateInput) => {
    if (!canModify) {
      duplicate({ variables: { id: props.id as string } })
        .then(() => navigate(TEMPLATE_DASHBOARD_ROUTE))
        .catch((err) => {
          logger.sendError(err);
          openSnackbar({ message: "Error copying template", status: "error" });
        });
      return;
    }
    updateTemplateGroupFunc({ variables: { data: templateData, id: props.id as string } })
      .then(() => {
        navigate(TEMPLATE_DASHBOARD_ROUTE);
      })
      .catch((err) => {
        logger.sendError(err);
        setServerError(true);
      });
  };

  if (data && data.templateGroup) {
    const templateData = data.templateGroup;
    const initialFormValues = {
      template: { name: template.name || "" },
      orgTemplate: template.isOrgTemplate,
      propertyTemplate: template.isPropertyTemplate,
      propertyIds: (template.properties || []).map(gqlUserToOption),
    };

    const messageProps = calculateMessageValues(templateData);
    return (
      <div className={styles.formContainer}>
        {renderErrorModal()}
        <div className="card">
          <div className={styles.formTitle}>
            <span className={styles.formTitleLink}>
              <Link data-cy="template-nav" to={TEMPLATE_DASHBOARD_ROUTE}>Templates</Link>
            </span>{" "}
            &gt; {canModify ? "Update Template" : "View Template"}
          </div>
          <div className={styles.templateFormContainer}>
            <TemplateForm
              initialValues={initialFormValues}
              isSending={updateTemplateGroupMutation.loading}
              handleSubmit={onSubmit}
              viewer={props.viewer}
              onCancel={() => navigate(TEMPLATE_DASHBOARD_ROUTE)}
              readOnly={!templateData.canModify}
              {...messageProps}
            />
          </div>
        </div>
      </div>
    );
  }

  if (loading) return <LoadingSpinner />;
  return <div>Error fetching template</div>;
};

export default UpdateTemplateGroups;
